import React, {Component} from 'react';
import {connect} from 'react-redux';
import {CircularProgress, Divider, IconButton, MenuItem, Select, Stack, Tooltip} from '@mui/material';
import CircleIcon from '@mui/icons-material/Circle';
import RemoveIcon from '@mui/icons-material/Remove';
import CallMadeIcon from '@mui/icons-material/CallMade';
import CreateIcon from '@mui/icons-material/Create';
import RouteIcon from '@mui/icons-material/Route';
import CircleOutlinedIcon from '@mui/icons-material/CircleOutlined';
import RectangleOutlinedIcon from '@mui/icons-material/RectangleOutlined';
import PentagonOutlinedIcon from '@mui/icons-material/PentagonOutlined';
import AutoFixHighOutlinedIcon from '@mui/icons-material/AutoFixHighOutlined';
import cookie from 'react-cookies';
import {AnnotationsConstants} from '../../../../utils/const';
import {updateGammaState, updateSelectedDrawColor, updateSelectedDrawWidth} from '../../../../redux_actions/gamma.state.action';
import {aclPermissions, hasPermissions} from '../../../../const/ACLPermissions';
import {notifySuccess} from '../../utils/display.notification';
import {notNull} from '../../../../utils/utils';
import {addKeyboardListener, isPermitted, removeKeyboardListeners} from '../../../../utils/listeners';
import {FEATURE_MODES, getFeatureMode} from '../featureRegistry';
import {requestOsdFeature} from '../viewerSelection';
import {TOOL_IDS} from './DrawInteractionOSD';

/** Same order, icons, tour classes and shortcut keys as the OL DrawTools. */
const TOOLS = [
    {id: TOOL_IDS.ARROW, name: 'Arrow', icon: <CallMadeIcon/>, className: 'Arrow-forTour',
        permission: aclPermissions.arrow_anno_app},
    {id: TOOL_IDS.LINE, name: 'Line', icon: <RemoveIcon sx={{transform: 'rotate(-45deg) scale(1.4)'}}/>,
        className: 'Line-forTour', shortcutKey: '1'},
    {id: TOOL_IDS.FREELINE, name: 'Freeline', icon: <CreateIcon/>, className: 'FreeLine-forTour', shortcutKey: '2'},
    {id: TOOL_IDS.FREEHAND, name: 'Free Loop', icon: <RouteIcon/>, className: 'FreeHand-forTour', shortcutKey: '4'},
    {id: TOOL_IDS.CIRCLE, name: 'Circle', icon: <CircleOutlinedIcon/>, className: 'Circle-forTour', shortcutKey: '3'},
    {id: TOOL_IDS.RECTANGLE, name: 'Rectangle', icon: <RectangleOutlinedIcon/>, className: 'Rectangle-forTour',
        shortcutKey: '5'},
    {id: TOOL_IDS.POLYGON, name: 'Polygon', icon: <PentagonOutlinedIcon/>, className: 'Polygon-forTour', shortcutKey: '6'},
    {id: TOOL_IDS.MAGIC_TOOL, name: 'MagicTool', icon: <AutoFixHighOutlinedIcon/>, className: 'MagicTool-forTour',
        shortcutKey: '7', feature: 'magic_tool'},
];

/**
 * Annotation toolbar for OSD maps. The active tool lives in `gammaState.activeDrawTool` and is
 * drawn by AnnotationOverlayOSD on the active map.
 */
class DrawToolsOSD extends Component {
    state = {isColorTooltipOpen: false, isWidthTooltipOpen: false, isDefaultColorPresent: false};

    componentDidMount() {
        const defaultColor = (cookie.load('organisation_setting') || {})['default_annotation_color'];
        if (notNull(defaultColor)) {
            this.props.dispatch(updateSelectedDrawColor(defaultColor));
            if (Object.values(AnnotationsConstants.ANNO_COLORS).includes(defaultColor)) this.setState({isDefaultColorPresent: true});
        }
        document.addEventListener('click', this.handleDocumentClick);
        addKeyboardListener(document, this.keyDown);
    }

    componentWillUnmount() {
        document.removeEventListener('click', this.handleDocumentClick);
        removeKeyboardListeners(document, this.keyDown);
        if (this.props.activeDrawTool) this.props.dispatch(updateGammaState({activeDrawTool: null}));
    }

    handleDocumentClick = (event) => {
        if (this.state.isColorTooltipOpen && !document.getElementById('anno-colors')?.contains(event.target)) {
            this.setState({isColorTooltipOpen: false});
        }
        if (this.state.isWidthTooltipOpen && !document.getElementById('anno-widths')?.contains(event.target)) {
            this.setState({isWidthTooltipOpen: false});
        }
    };

    getVisibleTools = () => TOOLS.filter(tool =>
        (!tool.permission || hasPermissions([tool.permission])) &&
        (!tool.feature || getFeatureMode(tool.feature, this.props.osdViewerConfig) !== FEATURE_MODES.HIDDEN));

    keyDown = (event) => {
        const tool = this.getVisibleTools().find(t => t.shortcutKey && t.shortcutKey === event.key);
        if (tool && isPermitted(event)) this.toggle(tool);
    };

    toggle = (tool) => {
        if (this.props.activeDrawTool === tool.id) {
            this.props.dispatch(updateGammaState({activeDrawTool: null}));
            return;
        }
        // A registry fallback for drawing switches the map to OpenLayers, where the OL tools take over.
        const feature = getFeatureMode('annotations', this.props.osdViewerConfig) !== FEATURE_MODES.NATIVE
            ? 'annotations' : tool.feature;
        const ready = feature ? requestOsdFeature(feature) : Promise.resolve(true);
        ready.then(proceed => {
            if (!proceed || (feature && getFeatureMode(feature, this.props.osdViewerConfig) !== FEATURE_MODES.NATIVE)) return;
            this.props.dispatch(updateGammaState({activeDrawTool: tool.id}));
            notifySuccess('drawTool', tool.id === TOOL_IDS.ARROW
                ? 'Click on the same point again to complete the Arrow'
                : 'Press Escape/Right Click/Click on annotation tool again to stop drawing annotations');
        });
    };

    renderColorPicker = () => (
        <Tooltip title="Colors" placement="bottom" id="anno-colors" className="Colours-forTour"
                 onMouseEnter={() => this.setState({isColorTooltipOpen: true})}
                 onMouseLeave={() => this.setState({isColorTooltipOpen: false})}
                 open={this.state.isColorTooltipOpen}>
            <Select variant="filled" value={this.props.selectedDrawColor.toLowerCase()}
                    onChange={e => this.props.dispatch(updateSelectedDrawColor(e.target.value))}>
                {!this.state.isDefaultColorPresent && (
                    <MenuItem value={this.props.selectedDrawColor.toLowerCase()}>
                        <CircleIcon sx={{color: this.props.selectedDrawColor.toLowerCase()}}/>
                    </MenuItem>
                )}
                {Object.values(AnnotationsConstants.ANNO_COLORS).map(color => (
                    <MenuItem key={color} value={color.toLowerCase()}>
                        <CircleIcon sx={{color: color.toLowerCase()}}/>
                    </MenuItem>
                ))}
            </Select>
        </Tooltip>
    );

    renderWidthPicker = () => (
        <Tooltip title="Anno Width" placement="bottom" id="anno-widths" className="Anno-Width-forTour"
                 onMouseEnter={() => this.setState({isWidthTooltipOpen: true})}
                 onMouseLeave={() => this.setState({isWidthTooltipOpen: false})}
                 open={this.state.isWidthTooltipOpen}>
            <Select variant="filled" value={this.props.selectedAnnoWidth}
                    onChange={e => this.props.dispatch(updateSelectedDrawWidth(e.target.value))}>
                {Object.values(AnnotationsConstants.ANNO_WIDTH).map(width => (
                    <MenuItem key={width} value={width}>
                        <RemoveIcon sx={{fontSize: '24px', stroke: '#fff', strokeWidth: `${width}`}}/>
                    </MenuItem>
                ))}
            </Select>
        </Tooltip>
    );

    renderTool = (tool) => {
        const loading = tool.id === TOOL_IDS.MAGIC_TOOL && this.props.magicToolDataRetrievalOngoing;
        return (
            <Tooltip key={tool.id} title={tool.name} placement="bottom">
                <IconButton id={tool.id} className={tool.className} onClick={() => this.toggle(tool)} sx={{minWidth: 40}}
                            color={this.props.activeDrawTool === tool.id ? 'secondary' : 'text'}>
                    {loading ? <CircularProgress color="secondary" size={22}/> : tool.icon}
                </IconButton>
            </Tooltip>
        );
    };

    render() {
        return (
            <Stack direction="row" spacing={1}>
                <Divider orientation="vertical" flexItem/>
                {this.renderColorPicker()}
                {hasPermissions([aclPermissions.anno_width_app]) && this.renderWidthPicker()}
                {this.getVisibleTools().map(this.renderTool)}
                <Divider orientation="vertical" flexItem/>
            </Stack>
        );
    }
}

const mapStateToProps = (state) => {
    const {activeDrawTool, selectedDrawColor, selectedAnnoWidth, magicToolDataRetrievalOngoing} = state.gammaStateReducer;
    return {
        activeDrawTool: activeDrawTool || null,
        selectedDrawColor: selectedDrawColor || AnnotationsConstants.DEFAULT_ANNO_COLOR,
        selectedAnnoWidth: selectedAnnoWidth || 2,
        magicToolDataRetrievalOngoing: !!magicToolDataRetrievalOngoing,
        osdViewerConfig: state.osdViewerConfigReducer,
    };
};

export default connect(mapStateToProps)(DrawToolsOSD);
